/**
 * Centralized helper for managing dimension-related ID strings.
 * All dimension ID generation, parsing, and type checking should go through this module.
 */

export type DimensionType =
  | 'width'
  | 'height'
  | 'segment'
  | 'left-edge'
  | 'right-edge'
  | 'top-width'
  | 'bottom-width';

export type DimensionIdInfo = {
  elementId: string;
  dimensionType: DimensionType;
  segmentIndex?: number;
};

/**
 * Creates a dimension ID string
 */
export const createDimensionId = (
  elementId: string,
  dimensionType: DimensionType,
  segmentIndex?: number
): string => {
  if (dimensionType === 'segment') {
    if (segmentIndex === undefined) {
      throw new Error(
        'segmentIndex is required when dimensionType is "segment"'
      );
    }
    return `${elementId}-dimension-segment-${segmentIndex}`;
  }
  if (dimensionType === 'left-edge' || dimensionType === 'right-edge') {
    return `${elementId}-dimension-${dimensionType}`;
  }
  return `${elementId}-dimension-${dimensionType}`;
};

/**
 * Checks if an ID string represents a dimension handle
 */
export const isDimensionId = (id: string): boolean => {
  return id.includes('-dimension-');
};

/**
 * Parses a dimension ID string and extracts its components
 * Returns null if the ID is not a valid dimension ID
 */
export const parseDimensionId = (id: string): DimensionIdInfo | null => {
  if (!isDimensionId(id)) return null;

  const parts = id.split('-dimension-');
  const elementId = parts[0];
  const typeInfo = parts[1];

  if (typeInfo === 'width') {
    return { elementId, dimensionType: 'width' };
  }

  if (typeInfo === 'height') {
    return { elementId, dimensionType: 'height' };
  }

  if (typeInfo === 'left-edge') {
    return { elementId, dimensionType: 'left-edge' };
  }

  if (typeInfo === 'right-edge') {
    return { elementId, dimensionType: 'right-edge' };
  }

  if (typeInfo === 'top-width') {
    return { elementId, dimensionType: 'top-width' };
  }

  if (typeInfo === 'bottom-width') {
    return { elementId, dimensionType: 'bottom-width' };
  }

  if (typeInfo.startsWith('segment-')) {
    const segmentIndex = parseInt(typeInfo.replace('segment-', ''), 10);
    if (isNaN(segmentIndex)) return null;
    return {
      elementId,
      dimensionType: 'segment',
      segmentIndex,
    };
  }

  return null;
};

/**
 * Extracts just the dimension type from an ID
 */
export const getDimensionType = (id: string): DimensionType | null => {
  const info = parseDimensionId(id);
  return info?.dimensionType ?? null;
};

/**
 * Extracts just the element ID from a dimension ID
 */
export const getElementIdFromDimensionId = (id: string): string | null => {
  const info = parseDimensionId(id);
  return info?.elementId ?? null;
};

/**
 * Extracts the segment index from a dimension ID (if applicable)
 */
export const getSegmentIndex = (id: string): number | null => {
  const info = parseDimensionId(id);
  return info?.segmentIndex ?? null;
};
